import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authStorage';

import API_BASE_URL from '../config/apiBaseUrl';
import {
  DEFAULT_FOOD_DETAIL_MACROS,
  DEFAULT_FOOD_MAIN_MACROS,
  SUMMARY_ESSENTIAL_MACROS,
} from '../config/nutritionMacros';
import { LEGACY_FOOD_MACRO_KEY_MAP } from '../config/openFoodFactsNutrients';

class SettingsJournalService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = getAuthToken();
  }

  getHeaders(contentType = 'application/json') {
    const freshToken = getAuthToken();
    if (freshToken) {
      this.token = freshToken;
    }

    const headers = {
      'Content-Type': contentType,
      'Accept': 'application/json',
    };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `Erreur HTTP! status: ${response.status}`);
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const mergedHeaders = {
      ...this.getHeaders(),
      ...(options.headers || {})
    };
    const config = {
      ...options,
      headers: mergedHeaders,
    };
    try {
      const response = await fetch(url, config);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur requête API Settings:', error);
      throw error;
    }
  }

  async getUserJournal() {
    try {
      // GET /journals retourne directement le journal (pas un tableau)
      const journal = await this.request('/journals');

      if (journal && journal.id) {
        return journal;
      }

      throw new Error('Aucun journal trouvé pour cet utilisateur');
    } catch (error) {
      console.error('Erreur lors du chargement du journal:', error);
      throw error;
    }
  }

  async getJournalById(journalId) {
    try {
      return await this.request(`/journals/${journalId}`);
    } catch (error) {
      console.error('Erreur lors du chargement du journal:', error);
      throw error;
    }
  }

  async getJournalSettings(journalId) {
    try {
      const response = await this.request(`/journal-settings?journal=${journalId}`);
      let settings;
      if (Array.isArray(response)) {
        settings = response;
      } else if (response['hydra:member']) {
        settings = response['hydra:member'];
      } else {
        settings = [];
      }
      return settings;
    } catch (error) {
      console.error('Erreur lors du chargement des settings:', error);
      throw error;
    }
  }

  async loadUserJournal() {
    try {
      const journal = await this.getUserJournal();
      const settings = await this.getJournalSettings(journal.id);

      let mealsWithFoods = [];
      if (journal.meals && journal.meals.length > 0) {
        mealsWithFoods = journal.meals;
      }

      return {
        id: journal.id,
        meals: mealsWithFoods,
        settings: this.parseSettings(settings),
        createdAt: journal.createdAt,
        updatedAt: journal.updatedAt
      };
    } catch (error) {
      console.error('Erreur lors du chargement du journal utilisateur:', error);
      throw error;
    }
  }

  parseSettings(settingsArray) {
    const settings = {};
    if (Array.isArray(settingsArray)) {
      settingsArray.forEach(setting => {
        if (setting.key) {
          settings[setting.key] = {
            id: setting.id,
            value: setting.value
          };
        }
      });
    }
    return settings;
  }

  getSettingValue(settings, key, defaultValue = null) {
    if (settings[key]) {
      return settings[key].value;
    }
    return defaultValue;
  }

  parseBoolean(value, defaultValue = false) {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase().replace(/^['"]+|['"]+$/g, '');
      if (['true', '1', 'oui', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'non', 'no'].includes(normalized)) return false;

      try {
        const parsed = JSON.parse(value);
        return this.parseBoolean(parsed, defaultValue);
      } catch {
        return defaultValue;
      }
    }
    return defaultValue;
  }

  normalizeSettingValue(key, value) {
    if (
      key === 'public_journal'
      || key === 'waterTracking'
      || key === 'hideUnavailableFoodMacros'
      || key === 'advancedMacroGoalsEnabled'
    ) {
      const normalized = this.parseBoolean(value, false);
      return normalized ? 'true' : 'false';
    }
    return value;
  }

  normalizeMacros(value, fallback = []) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore JSON parse errors
      }
    }
    return fallback;
  }

  mapLegacyFoodMacroKeys(macros = []) {
    const mapped = macros.map((macro) => LEGACY_FOOD_MACRO_KEY_MAP[macro] || macro);
    return Array.from(new Set(mapped));
  }

  mapLegacySummaryMacroKeys(macros = []) {
    const legacyMap = {
      calories: 'energy-kcal',
      protein: 'proteins',
      carbs: 'carbohydrates',
      fats: 'fat'
    };
    const mapped = macros.map((macro) => legacyMap[macro] || LEGACY_FOOD_MACRO_KEY_MAP[macro] || macro);
    return Array.from(new Set(mapped));
  }

  async getNutritionalGoals(journalId) {
    try {
      const settingsArray = await this.getJournalSettings(journalId);
      const settings = this.parseSettings(settingsArray);
      const publicRaw = this.getSettingValue(settings, 'public_journal', 'true');
      const publicJournal = this.parseBoolean(publicRaw, true);
      const parseOptionalPositiveNumber = (key) => {
        const value = Number(this.getSettingValue(settings, key, ''));
        return Number.isFinite(value) && value > 0 ? value : null;
      };

      return {
        targetCalories: parseInt(this.getSettingValue(settings, 'targetCalories', '2000')),
        targetProtein: parseInt(this.getSettingValue(settings, 'targetProtein', '150')),
        targetCarbs: parseInt(this.getSettingValue(settings, 'targetCarbs', '250')),
        targetFats: parseInt(this.getSettingValue(settings, 'targetFats', '70')),
        advancedMacroGoalsEnabled: this.parseBoolean(
          this.getSettingValue(settings, 'advancedMacroGoalsEnabled', 'false'),
          false
        ),
        targetFiber: parseOptionalPositiveNumber('targetFiber'),
        targetSugars: parseOptionalPositiveNumber('targetSugars'),
        targetSodium: parseOptionalPositiveNumber('targetSodium'),
        targetSaturatedFat: parseOptionalPositiveNumber('targetSaturatedFat'),
        waterTarget: parseInt(this.getSettingValue(settings, 'waterTarget', '2000')),
        waterTracking: this.parseBoolean(this.getSettingValue(settings, 'waterTracking', 'true'), true),
        displayedMacros: this.mapLegacyFoodMacroKeys(
          this.normalizeMacros(
            this.getSettingValue(settings, 'displayedMacros', DEFAULT_FOOD_DETAIL_MACROS),
            DEFAULT_FOOD_DETAIL_MACROS
          )
        ),
        mainMacros: this.mapLegacyFoodMacroKeys(
          this.normalizeMacros(
            this.getSettingValue(settings, 'mainMacros', DEFAULT_FOOD_MAIN_MACROS),
            DEFAULT_FOOD_MAIN_MACROS
          )
        ),
        summaryMacros: this.mapLegacySummaryMacroKeys(
          this.normalizeMacros(
            this.getSettingValue(settings, 'summaryMacros', SUMMARY_ESSENTIAL_MACROS),
            SUMMARY_ESSENTIAL_MACROS
          )
        ),
        hideUnavailableFoodMacros: this.parseBoolean(
          this.getSettingValue(settings, 'hideUnavailableFoodMacros', 'false'),
          false
        ),
        publicJournal
      };
    } catch (error) {
      console.error('Erreur lors du chargement des objectifs:', error);
      return {
        targetCalories: 2000,
        targetProtein: 150,
        targetCarbs: 250,
        targetFats: 70,
        advancedMacroGoalsEnabled: false,
        targetFiber: null,
        targetSugars: null,
        targetSodium: null,
        targetSaturatedFat: null,
        waterTarget: 2000,
        waterTracking: true,
        displayedMacros: [...DEFAULT_FOOD_DETAIL_MACROS],
        mainMacros: [...DEFAULT_FOOD_MAIN_MACROS],
        summaryMacros: [...SUMMARY_ESSENTIAL_MACROS],
        hideUnavailableFoodMacros: false,
        publicJournal: true
      };
    }
  }

  async updateSetting(journalId, key, value) {
    try {
      if (!journalId) {
        throw new Error('Journal introuvable. Rechargez la page avant de sauvegarder.');
      }

      const normalizedValue = this.normalizeSettingValue(key, value);
      const settingsArray = await this.getJournalSettings(journalId);
      const settings = this.parseSettings(settingsArray);
      const existingSetting = settings[key];

      if (existingSetting) {
        await this.request(`/journal-settings/${existingSetting.id}`, {
          method: 'PATCH',
          headers: this.getHeaders('application/merge-patch+json'),
          body: JSON.stringify({ value: normalizedValue })
        });
      } else {
        await this.request('/journal-settings', {
          method: 'POST',
          body: JSON.stringify({
            journal: `/api/journals/${journalId}`,
            key: key,
            value: normalizedValue
          })
        });
      }

      return { success: true };
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du setting ${key}:`, error);
      throw error;
    }
  }

  // Alias pour updateSetting (utilisé par NutritionSetting)
  async updateJournalSetting(journalId, key, value) {
    return await this.updateSetting(journalId, key, value);
  }

  async updateNutritionalGoals(journalId, goals) {
    try {
      const toRequiredGoalValue = (value, fallback = null) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
          return fallback;
        }
        return Math.round(numeric).toString();
      };

      const toOptionalGoalValue = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
          return '';
        }
        return Math.round(numeric).toString();
      };

      const updates = [
        this.updateSetting(journalId, 'targetCalories', toRequiredGoalValue(goals.targetCalories, '2000')),
        this.updateSetting(journalId, 'targetProtein', toRequiredGoalValue(goals.targetProtein, '150')),
        this.updateSetting(journalId, 'targetCarbs', toRequiredGoalValue(goals.targetCarbs, '250')),
        this.updateSetting(journalId, 'targetFats', toRequiredGoalValue(goals.targetFats, '70')),
        this.updateSetting(journalId, 'advancedMacroGoalsEnabled', Boolean(goals.advancedMacroGoalsEnabled)),
        this.updateSetting(journalId, 'targetFiber', toOptionalGoalValue(goals.targetFiber)),
        this.updateSetting(journalId, 'targetSugars', toOptionalGoalValue(goals.targetSugars)),
        this.updateSetting(journalId, 'targetSodium', toOptionalGoalValue(goals.targetSodium)),
        this.updateSetting(journalId, 'targetSaturatedFat', toOptionalGoalValue(goals.targetSaturatedFat))
      ];
      await Promise.all(updates);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour des objectifs nutritionnels:', error);
      throw error;
    }
  }

  async updateDisplayedMacros(journalId, macros) {
    return await this.updateSetting(journalId, 'displayedMacros', macros);
  }

  async updateMainMacros(journalId, macros) {
    return await this.updateSetting(journalId, 'mainMacros', macros);
  }

  async updateSummaryMacros(journalId, macros) {
    return await this.updateSetting(journalId, 'summaryMacros', macros);
  }

  async updateHideUnavailableFoodMacros(journalId, enabled) {
    return await this.updateSetting(journalId, 'hideUnavailableFoodMacros', enabled ? 'true' : 'false');
  }

  async updateWaterTracking(journalId, enabled) {
    return await this.updateSetting(journalId, 'waterTracking', enabled ? 'true' : 'false');
  }

  async updateWaterTarget(journalId, target) {
    return await this.updateSetting(journalId, 'waterTarget', target.toString());
  }

  async getAllSettings(journalId) {
    try {
      const settingsArray = await this.getJournalSettings(journalId);
      const parsed = {};
      settingsArray.forEach(s => {
        parsed[s.key] = s.value;
      });
      return parsed;
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      throw error;
    }
  }

  async updateMultipleSettings(journalId, settingsObject) {
    try {
      const updates = Object.entries(settingsObject).map(([key, value]) =>
        this.updateSetting(journalId, key, value)
      );
      await Promise.all(updates);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour multiple des paramètres:', error);
      throw error;
    }
  }

  updateAuthToken(newToken) {
    this.token = newToken;
    setAuthToken(newToken);
  }

  clearAuthToken() {
    this.token = null;
    clearAuthToken();
  }
}

const settingsJournalService = new SettingsJournalService();

export default settingsJournalService;
